import { HeapPHIDecorator } from "@app/components/analytics";
import React, { FC, useMemo } from "react";
import { defineMessages, useIntl } from "react-intl";
import { Button, DisplayField, Form } from "@natera/form";
import { FormField, LinkHeap, Notification } from "@app/components";
import { ConfigContext, DrawRequestContext } from "@app/provider";
import { getFormattedToUTCDate, getProfileFullName } from "@app/utils";

import { ScheduleBloodDrawScreens } from "../scheduleBloodDraw";

import { HEAP_EVENTS, HeapEventLocation } from "@app/provider/types";
import {
  getReadableComboOrderTestTypesList,
  getReadableComboOrderTestTypesString,
} from "@app/utils";

import "./personalInformation.scss";

const messages = defineMessages({
  scheduleBloodPersonalInfoNotification: {
    id: "scheduleBloodPersonalInfoUpdateNote",
    defaultMessage:
      "If you need to make changes to your personal information {contactUsLink}",
  },
  scheduleBloodPersonalInfoContactUs: {
    id: "scheduleBloodPersonalInfoContactUs",
    defaultMessage: "contact us",
  },
  scheduleBloodPersonalInfoTitle: {
    id: "scheduleBloodPersonalInfoTitle",
    defaultMessage: "Personal Information",
  },
  scheduleBloodPersonalInfoFormFullName: {
    id: "scheduleBloodPersonalInfoFormFullName",
    defaultMessage: "Full Name",
  },
  scheduleBloodPersonalInfoFormDateOfBirth: {
    id: "scheduleBloodPersonalInfoFormDateOfBirth",
    defaultMessage: "Date of Birth",
  },
  scheduleBloodPersonalInfoTestType: {
    id: "scheduleBloodPersonalInfoTestType",
    defaultMessage: "Test Type / Name",
  },
  scheduleBloodPersonalInfoOrderedDate: {
    id: "scheduleBloodPersonalInfoOrderedDate",
    defaultMessage: "Ordered Date",
  },
  scheduleBloodPersonalInfoNext: {
    id: "personalInfoNext",
    defaultMessage: "Next",
  },
  scheduleBloodPersonalInfoPhysician: {
    id: "scheduleBloodPersonalInfoPhysician",
    defaultMessage: "Ordered By",
  },
});

interface PersonalInformationProps {
  setBloodDrawScreen: React.Dispatch<
    React.SetStateAction<ScheduleBloodDrawScreens>
  >;
}

export const PersonalInformation: FC<PersonalInformationProps> = ({
  setBloodDrawScreen,
}) => {
  const intl = useIntl();
  const { config } = React.useContext(ConfigContext);
  const { verifiedSampleDrawData } = React.useContext(DrawRequestContext);

  const profileInfo = verifiedSampleDrawData?.profileInfo.profile;
  const sampleInfo = verifiedSampleDrawData?.sampleInfo.sample;
  const physician = sampleInfo?.physician;

  const testTypes = sampleInfo?.testTypes;

  const readableTestTypesList = getReadableComboOrderTestTypesList(
    testTypes ?? []
  );

  const readableTestTypes = useMemo(() => {
    return getReadableComboOrderTestTypesString(readableTestTypesList, intl);
  }, [readableTestTypesList, intl]);

  const onSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    setBloodDrawScreen(ScheduleBloodDrawScreens.KIT_DETAILS_SCREEN);
  };

  return (
    <div className="schedule-blood-draw-personal-info">
      <div className="personal-info-content">
        <Notification
          className="personal-info-content__notification"
          type="note"
        >
          {intl.formatMessage(messages.scheduleBloodPersonalInfoNotification, {
            contactUsLink: (
              <LinkHeap
                target="_blank"
                key="privacyPolicy"
                rel="noopener noreferrer"
                to={config.links.CONTACT_US}
                heapEventName={HEAP_EVENTS.upp_click_contactnatera}
                heapEventProps={{
                  location: HeapEventLocation.personal_info_mp_page,
                }}
              >
                {intl.formatMessage(
                  messages.scheduleBloodPersonalInfoContactUs
                )}
              </LinkHeap>
            ),
          })}
        </Notification>
        <h3 className="personal-info-content__title">
          {intl.formatMessage(messages.scheduleBloodPersonalInfoTitle)}
        </h3>
        <Form
          className="personal-info-content__form"
          noValidate
          onSubmit={onSubmit}
        >
          <FormField
            label={intl.formatMessage(
              messages.scheduleBloodPersonalInfoFormFullName
            )}
            htmlFor="fullname"
            withPadding
          >
            <HeapPHIDecorator protectText>
              <DisplayField
                id="fullname"
                value={getProfileFullName(profileInfo)}
              />
            </HeapPHIDecorator>
          </FormField>
          <FormField
            label={intl.formatMessage(
              messages.scheduleBloodPersonalInfoFormDateOfBirth
            )}
            htmlFor="dateOfBirth"
            withPadding
          >
            <HeapPHIDecorator protectText>
              <DisplayField
                id="dateOfBirth"
                value={getFormattedToUTCDate(profileInfo?.dateOfBirth)}
              />
            </HeapPHIDecorator>
          </FormField>
          <FormField
            label={intl.formatMessage(
              messages.scheduleBloodPersonalInfoTestType
            )}
            htmlFor="testType"
            withPadding
          >
            <HeapPHIDecorator protectText>
              <DisplayField
                id="testType"
                value={readableTestTypes ?? <>&mdash;</>}
              />
            </HeapPHIDecorator>
          </FormField>

          <FormField
            label={intl.formatMessage(
              messages.scheduleBloodPersonalInfoPhysician
            )}
            htmlFor="physician"
            withPadding
          >
            <HeapPHIDecorator protectText>
              <DisplayField id="physician" value={physician ?? <>&mdash;</>} />
            </HeapPHIDecorator>
          </FormField>

          <FormField
            label={intl.formatMessage(
              messages.scheduleBloodPersonalInfoOrderedDate
            )}
            htmlFor="orderedDate"
            withPadding
          >
            <HeapPHIDecorator protectText>
              <DisplayField
                id="orderedDate"
                value={
                  getFormattedToUTCDate(sampleInfo?.creationDate) || (
                    <>&mdash;</>
                  )
                }
              />
            </HeapPHIDecorator>
          </FormField>
          <div className="personal-info-content__form-actions">
            <Button type="submit" data-testid="submit" unelevated>
              {intl.formatMessage(messages.scheduleBloodPersonalInfoNext)}
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
};
